import React, { useState, useMemo } from 'react';
import {
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  TableColumnDefinition,
  TableRowId,
  createTableColumn,
} from '@fluentui/react-table';
import { CheckmarkFilled, DismissFilled, CalendarPersonRegular } from '@fluentui/react-icons';
import {
  Button,
  Field,
  Input,
} from '@fluentui/react-components';
import "./styles.scss";
import Paginator from '#root/components/Paginator';
import { Breadcrumbs } from '#root/components/Breadcrumbs';
import { showToast } from "#root/components/GlobalToaster";
import {
  useAccountRequests,
  useBatchProcessAccountRequests,
  AccountRequest,
} from '#root/queries/accountRequests';
import LoadingWrapper from "#root/components/LoadingWrapper";
import { DialogBox } from "#root/components/DialogBox";
import { ROLES } from '#root/utils/roles';
import ButtonGroup from '#root/components/ButtonGroup';
import { toSentenceCase } from '#root/services/utils';

const availableRoles = Object.values(ROLES).filter(role => role !== ROLES.GLOBALADMIN);

const UserAccountRequests: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { data: accountRequestsData, isLoading, refetch } = useAccountRequests({
    skip: (currentPage - 1) * itemsPerPage,
    take: itemsPerPage,
  });

  const accountRequests = accountRequestsData?.items || [];
  const totalItems = accountRequestsData?.totalCount || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const [selectedItems, setSelectedItems] = useState<Set<TableRowId>>(new Set());
  const handleSelectionChange = (
    _e: React.MouseEvent | React.KeyboardEvent,
    data: { selectedItems: Set<TableRowId> },
  ) => {
    setSelectedItems(data.selectedItems);
  };

  const { mutate: batchProcess, isPending: isBatchProcessing } = useBatchProcessAccountRequests();

  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<AccountRequest | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>(availableRoles[1]);

  const openApproveModal = (req: AccountRequest) => {
    setSelectedRequest(req);
    setSelectedRole(availableRoles[1]);
    setIsApproveModalOpen(true);
  };

  const closeApproveModal = () => {
    setIsApproveModalOpen(false);
    setSelectedRequest(null);
  };

  const handleModalApprove = () => {
    if (!selectedRequest) return;
    batchProcess(
      {
        ids: [selectedRequest.id!],
        isApproved: true,
        role: selectedRole,
      },
      {
        onSuccess: () => {
          refetch();
          closeApproveModal();
          showToast('User Account Request Approved');
        },
      },
    );
  };

  const [isDenyDialogOpen, setIsDenyDialogOpen] = useState(false);
  const [denyIds, setDenyIds] = useState<string[]>([]);

  const openDenyDialog = (ids: string | string[]) => {
    const idsArray = typeof ids === 'string' ? [ids] : ids;
    setDenyIds(idsArray);
    setIsDenyDialogOpen(true);
  };

  const closeDenyDialog = () => {
    setIsDenyDialogOpen(false);
    setDenyIds([]);
  };

  const handleConfirmDeny = () => {
    if (denyIds.length === 0) return;
    batchProcess(
      {
        ids: denyIds,
        isApproved: false,
      },
      {
        onSuccess: () => {
          closeDenyDialog();
          setSelectedItems(new Set());
          refetch();
          showToast('User Account Request Denied');
        },
      },
    );
  };

  const columns: TableColumnDefinition<AccountRequest>[] = [
    createTableColumn<AccountRequest>({
      columnId: 'firstName',
      renderHeaderCell: () => 'First Name',
      renderCell: (item) => (
        <div title={item.firstName}>
          {item.firstName}
        </div>
      ),
    }),
    createTableColumn<AccountRequest>({
      columnId: 'lastName',
      renderHeaderCell: () => 'Last Name',
      renderCell: (item) => (
        <div title={item.lastName}>
          {item.lastName}
        </div>
      ),
    }),
    createTableColumn<AccountRequest>({
      columnId: 'email',
      renderHeaderCell: () => 'Email',
      renderCell: (item) => (
        <div title={item.email}>
          {item.email}
        </div>
      ),
    }),
    createTableColumn<AccountRequest>({
      columnId: 'phoneNumber',
      renderHeaderCell: () => 'Phone',
      renderCell: (item) => (
        <div title={item.phoneNumber}>
          {item.phoneNumber}
        </div>
      ),
    }),
    createTableColumn<AccountRequest>({
      columnId: 'approve',
      renderHeaderCell: () => 'Approve',
      renderCell: (item) => (
        <>
          <Button
            appearance="primary"
            icon={<CheckmarkFilled />}
            onClick={(e) => {
              e.stopPropagation();
              openApproveModal(item);
            }}
          />
          <Button
            appearance="secondary"
            icon={<DismissFilled />}
            onClick={(e) => {
              e.stopPropagation();
              openDenyDialog(item.id!);
            }}
          />
        </>
      )
    }
    )
  ];


  return (
    <div className="user-account-requests-page">
      <div className="emc-page-header">
        <div className="emc-page-header__top-row">
          <div className="emc-page-header__breadcrumbs">
            <Breadcrumbs />
          </div>
        </div>
        <div className="emc-page-header__title">
          <h2>
            User Account Requests <div className="divider"></div>
          </h2>
        </div>
      </div>

      <LoadingWrapper
        isLoading={isLoading}
        isEmpty={accountRequests.length === 0}
        emptyMessage="No user account requests found"
        emptyIcon={<CalendarPersonRegular />}
      >
        <div className="emc-page-content">
          <div className="emc-grid">
            <DataGrid
              className="emc-datagrid"
              items={accountRequests}
              columns={columns}
              selectionMode="multiselect"
              selectedItems={selectedItems}
              onSelectionChange={handleSelectionChange}
              getRowId={(item) => item.id!}
              focusMode="none"
            >
              <DataGridHeader>
                <DataGridRow>
                  {({ renderHeaderCell, columnId }) => (
                    <DataGridHeaderCell className={columnId.toString()}>
                      {renderHeaderCell()}
                    </DataGridHeaderCell>
                  )}
                </DataGridRow>
              </DataGridHeader>

              <DataGridBody<AccountRequest>>
                {({ item, rowId }) => (
                  <DataGridRow<AccountRequest>
                    key={rowId}
                    selectionCell={{ "aria-label": "Select row" }}
                  >
                    {({ renderCell, columnId }) => (
                      <DataGridCell className={columnId.toString()}>
                        {renderCell(item)}
                      </DataGridCell>
                    )}
                  </DataGridRow>
                )}
              </DataGridBody>
            </DataGrid>
          </div>
        </div>

        <Paginator
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(newPage) => {
            setCurrentPage(newPage);
            refetch();
          }}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={(newItemsPerPage) => {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
            refetch();
          }}
          totalItems={totalItems}
          currentItems={(currentPage - 1) * itemsPerPage + 1}
          showItemsPerPage={true}
        />
        {selectedItems.size > 0 && (
          <div className="emc-page-footer">
            <Button appearance="secondary" onClick={() => setSelectedItems(new Set())}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={isBatchProcessing || selectedItems.size === 0}
              onClick={() => openDenyDialog(Array.from(selectedItems) as string[])}
            >
              {isBatchProcessing ? 'Processing...' : 'Deny Selected'}
            </Button>
          </div>
        )}
      </LoadingWrapper>

      {selectedRequest && (
        <DialogBox
          open={isApproveModalOpen && selectedRequest !== null}
          onOpenChange={(open) => {
            if (!open) {
              closeApproveModal();
            }
          }}
          title="Approve Request"
          primaryButtonText="Approve"
          primaryButtonAppearance="primary"
          onPrimaryButtonClick={handleModalApprove}
          secondaryButtonText="Cancel"
          onSecondaryButtonClick={closeApproveModal}
          isPrimaryButtonLoading={isBatchProcessing}
          isPrimaryButtonDisabled={isBatchProcessing}
          isSecondaryButtonDisabled={isBatchProcessing}
        >
          <form>
            <div>
              Please choose the role you want to assign to the selected user
            </div>
            <Field label="First Name" className="readonly-input-acc-request">
              <Input readOnly value={selectedRequest.firstName || ''} />
            </Field>
            <Field label="Last Name" className="readonly-input-acc-request">
              <Input readOnly value={selectedRequest.lastName || ''} />
            </Field>
            <Field label="Email" className="readonly-input-acc-request">
              <Input readOnly value={selectedRequest.email || ''} />
            </Field>
            <Field label="Role" required>
              <ButtonGroup
                options={availableRoles.map(role => ({ label: toSentenceCase(role), value: role }))}
                value={selectedRole}
                onChange={setSelectedRole}
                name="role"
              />
            </Field>
          </form>
        </DialogBox>
      )}

      <DialogBox
        open={isDenyDialogOpen}
        onOpenChange={(open) => {
          if (!isBatchProcessing && !open) {
            closeDenyDialog();
          }
        }}
        title="Deny Request(s)"
        primaryButtonText="Deny"
        primaryButtonAppearance="primary"
        onPrimaryButtonClick={handleConfirmDeny}
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={closeDenyDialog}
        isPrimaryButtonLoading={isBatchProcessing}
        isPrimaryButtonDisabled={isBatchProcessing}
        isSecondaryButtonDisabled={isBatchProcessing}
      >
        <div style={{ marginBottom: '1rem' }}>
          Are you sure you want to deny the selected requests?
        </div>
      </DialogBox>
    </div>
  );
};

export default UserAccountRequests;